import { useState } from "react";
import { Box, Button, InputAdornment, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useCRUD } from "../../api/crud";
import { ERROR_SNACKBAR, JOB_SERVICES_ENDPOINT, SUCCESS_SNACKBAR } from "../../consts";
import {
  DECIMAL_FOUR_PLACES_INPUT_REGEX,
  formatDecimalForDisplay,
  formatDecimalForInput,
  formatMoney,
  toApiDecimal,
} from "../../utils";

const extractErrorMessage = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  if (typeof value === "object" && "message" in value) {
    return (value as { message?: string }).message ?? null;
  }
  return null;
};

interface JobServicePriceChangeFormProps {
  serviceId: number | string;
  /** The catalogue price the row shows today; empty when the service has none. */
  price: string | null;
  onSuccess?: () => void;
}

/**
 * Sets a service's net catalogue price. Tasks keep the price a service was
 * added at, so this only reaches services added to jobs from now on.
 */
export default function JobServicePriceChangeForm({ serviceId, price, onSuccess }: JobServicePriceChangeFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { update, loading } = useCRUD(JOB_SERVICES_ENDPOINT);
  // Start from what the service costs today, so a small change is a small edit.
  const [priceNet, setPriceNet] = useState(() => formatDecimalForInput(price));
  const [isPriceFocused, setIsPriceFocused] = useState(false);
  const [priceError, setPriceError] = useState<string | null>(null);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Strip any grouping first: a value pasted from a grouped display
    // ("1 234,56") would otherwise fail the plain-decimal check.
    const nextValue = e.target.value.replace(/[\s  ]/g, "");
    if (nextValue === "" || DECIMAL_FOUR_PLACES_INPUT_REGEX.test(nextValue)) {
      setPriceNet(nextValue);
    }
  };

  const handleSubmit = async () => {
    setPriceError(null);
    // An empty field clears the price, as in the full service form.
    const result = await update({ id: serviceId, price: priceNet ? toApiDecimal(priceNet) : null });

    if (result?.success) {
      enqueueSnackbar(t("Service price changed."), SUCCESS_SNACKBAR);
      onSuccess?.();
      return;
    }

    setPriceError(extractErrorMessage(result?.errorData?.price));
    enqueueSnackbar(
      extractErrorMessage(result?.errorData?.message) ||
        extractErrorMessage(result?.errorData?.detail) ||
        t("There was an error, please review the form."),
      ERROR_SNACKBAR
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          {t("Current Price")}
        </Typography>
        {price ? (
          <Typography variant="body1">{`${formatMoney(price)} ${t("Net")}`}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("Not set")}
          </Typography>
        )}
      </Box>

      <TextField
        size="small"
        label={t("New price net")}
        value={isPriceFocused ? priceNet : formatDecimalForDisplay(priceNet)}
        onChange={handlePriceChange}
        onFocus={() => setIsPriceFocused(true)}
        onBlur={() => setIsPriceFocused(false)}
        inputProps={{ inputMode: "decimal" }}
        InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
        fullWidth
        autoFocus
        error={Boolean(priceError)}
        helperText={priceError || t("Services already added to jobs keep the price they were added at.")}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" color="success" loading={loading} onClick={handleSubmit}>
          {t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
